import * as React from 'react'

import { motion } from 'motion/react'

import { cn } from '@/utils/tailwind-merge'

import { Icon } from '../../atoms/Icon'

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

/**
 * Width is set via inline style (so it can be a dynamic px or any CSS length)
 * and therefore cannot be overridden by a Tailwind class on `className`.
 * Use `maxWidth` / `maxWidthLarge` to control width; use `className` for
 * everything else (spacing, color, alignment, etc.).
 */
export interface TruncatedTextProps {
  maxWidth: number | string
  maxWidthLarge?: number | string
  children: React.ReactNode
  onExpansionChange?: (expanded: boolean) => void
  className?: string
}

const TRANSITION = {
  duration: 0.4,
  ease: [0.32, 0.72, 0, 1] as [number, number, number, number]
}

const LARGE_SCREEN_MEDIA_QUERY = '(min-width: 2560px)'

export function TruncatedText({
  maxWidth,
  maxWidthLarge,
  children,
  className,
  onExpansionChange
}: TruncatedTextProps): React.JSX.Element {
  const contentRef = React.useRef<HTMLSpanElement>(null)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [showFullText, setShowFullText] = React.useState(false)
  const [isOverflowing, setIsOverflowing] = React.useState(false)
  const [collapsedHeight, setCollapsedHeight] = React.useState<number>(0)
  const [isLargeScreen, setIsLargeScreen] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(LARGE_SCREEN_MEDIA_QUERY)
    const update = (): void => setIsLargeScreen(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useIsomorphicLayoutEffect(() => {
    const el = contentRef.current
    if (!el || showFullText) return

    const measure = (): void => {
      setCollapsedHeight(el.offsetHeight)
      setIsOverflowing(
        el.scrollHeight > el.offsetHeight + 1 ||
          el.scrollWidth > el.clientWidth + 1
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [children, showFullText])

  const effectiveMaxWidth =
    isLargeScreen && maxWidthLarge !== undefined ? maxWidthLarge : maxWidth
  const maxWidthStyle =
    typeof effectiveMaxWidth === 'number'
      ? `${effectiveMaxWidth}px`
      : effectiveMaxWidth

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    onExpansionChange?.(!isExpanded)
    if (isExpanded) {
      setIsExpanded(false)
    } else {
      setShowFullText(true)
      setIsExpanded(true)
    }
  }

  const shouldAnimate = collapsedHeight > 0

  return (
    <div
      className={cn('flex min-w-0 items-start gap-1', className)}
      style={{ width: maxWidthStyle, maxWidth: maxWidthStyle }}
    >
      <motion.div
        className='min-w-0 flex-1 overflow-hidden pr-1'
        initial={false}
        animate={
          shouldAnimate
            ? { height: isExpanded ? 'auto' : collapsedHeight }
            : undefined
        }
        transition={TRANSITION}
        onAnimationComplete={() => {
          if (!isExpanded) setShowFullText(false)
        }}
      >
        <span
          ref={contentRef}
          className={cn(
            'block max-w-full',
            showFullText
              ? 'whitespace-normal break-words'
              : 'truncate [&>*]:contents'
          )}
        >
          {children}
        </span>
      </motion.div>
      {isOverflowing && (
        <button
          type='button'
          onClick={handleToggle}
          className='shrink-0 cursor-pointer'
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <motion.span
            className='inline-flex'
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={TRANSITION}
          >
            <Icon name='expand_more' size='lg' />
          </motion.span>
        </button>
      )}
    </div>
  )
}
